import type { EventReminder } from 'models';
import { useMemo } from 'react';
import { Form, FormActions, FormLayout, Modal, message } from 'ui';
import {
  AttachmentsFormContent,
  type AttachmentsFormContentValues,
  DescriptionImageFormContent,
  type ExtendedDescriptionImageFormContentValues,
  type ExtendedReminderDetailFormContentValues,
  type ExtendedReminderDurationFormValues,
  ReminderDetailFormContent,
  ReminderDurationFormContent,
} from '../../components';
import { useDeleteEventAttachments, useUploadEventAttachments, useUploadEventCoverPhoto } from '../../hooks';
import { useEditEvent } from '../../queries';
import { useCreateFormRequestData, useGetInitialValues } from './hooks';

export type EditReminderFormValues = AttachmentsFormContentValues &
  ExtendedDescriptionImageFormContentValues &
  ExtendedReminderDetailFormContentValues &
  ExtendedReminderDurationFormValues;

type P = {
  event: EventReminder;
  onCancel: () => void;
  onSuccess: () => void;
};

const EditReminderForm = ({ event, onCancel, onSuccess }: P) => {
  const { isPending, mutate: editEvent } = useEditEvent();
  const { isUploadingAttachments, uploadEventAttachments } = useUploadEventAttachments();
  const { isUploadingCoverPhoto, uploadEventCoverPhoto } = useUploadEventCoverPhoto();
  const { deleteEventAttachments, isDeletingAttachments } = useDeleteEventAttachments();
  const initialValues = useGetInitialValues(event);
  const createFormRequestData = useCreateFormRequestData();

  const onSubmit = async (formValues: EditReminderFormValues) => {
    await deleteEventAttachments({ event, formAttachments: formValues.attachments });
    await uploadEventCoverPhoto({
      eventId: event.id,
      existingCoverPhotoId: event.coverPhoto,
      heroImage: formValues.heroImage,
    });
    await uploadEventAttachments({ attachments: formValues.attachments, eventId: event.id });
    editEvent(createFormRequestData(formValues, event.id), {
      onSuccess() {
        message.success('Reminder edited successfully.');
        onSuccess();
      },
    });
  };

  const isLoading = useMemo(
    () => isPending || isUploadingAttachments || isUploadingCoverPhoto || isDeletingAttachments,
    [isPending, isUploadingAttachments, isUploadingCoverPhoto, isDeletingAttachments],
  );

  return (
    <Form<EditReminderFormValues> initialValues={initialValues} onFinish={onSubmit}>
      <FormLayout>
        <ReminderDetailFormContent<EditReminderFormValues> />
        <ReminderDurationFormContent<EditReminderFormValues> />
        <DescriptionImageFormContent<EditReminderFormValues> />
        <AttachmentsFormContent<EditReminderFormValues> />
      </FormLayout>
      <Modal.HugContent.Footer>
        <FormActions
          right={{
            onClick: onCancel,
          }}
          submit={{
            children: 'Edit',
            loading: isLoading,
          }}
        />
      </Modal.HugContent.Footer>
    </Form>
  );
};

export default EditReminderForm;
